import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import {
  AlertTriangle, Truck, Send, CheckCircle2, Camera, Clock, Info, RefreshCw,
} from 'lucide-react';
import type { Vehicle } from '../../types/vehicle';
import type { LogbookEntry } from '../../types/logbook';
import { useLogbookTypes } from '../../hooks/useLogbookTypes';
import { useLogbookAttachments } from '../../hooks/useLogbookAttachments';
import { LogbookPhotoPicker } from '../logbook/LogbookPhotoPicker';
import { createLogbookEntry } from '../../lib/api/endpoints';
import { logbookEntryFromDto } from '../../lib/api/mappers';
import { colorDeTipo, mensajeErrorNovedad } from '../../utils/logbook';
import { formatDateTime, fromLocalInputValue, toLocalInputValue } from '../../utils/trips';

interface ReportNoveltyModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** El vehículo asignado al conductor. Sin él la novedad solo cuelga del conductor. */
  vehicle?: Vehicle;
  /** Se llama con la novedad que devolvió el servidor, no con una fabricada aquí. */
  onRegistrada?: (entry: LogbookEntry) => void;
}

/**
 * FE-13 — el reporte de novedad del Operador, contra el API real.
 *
 * Antes de esta épica esto era un `setTimeout(600)` que guardaba la novedad en
 * un `useState` de la pestaña: el operador veía "¡Novedad enviada!" y nadie
 * más se enteraba nunca. Es el mismo defecto que el issue #76, y aquí se
 * cierra igual — el éxito lo dice el `201` del servidor, y la hora que se
 * muestra es la que el servidor guardó.
 *
 * **El Operador no elige de quién es la novedad.** El backend le estampa su
 * propio `driverId` pase lo que pase en el cuerpo (§4.8), y no es cosmético:
 * sin eso la novedad nacería fuera de su propia visibilidad —la crearía y no
 * volvería a verla nunca—. Lo que sí se manda es su vehículo asignado, que el
 * servidor no adivina.
 *
 * **`occurredAt` se puede mover al pasado.** Una varada que se relata al
 * llegar, dos horas después, pertenece a la hora en que ocurrió: es lo que
 * decide en qué viaje entra (ADR-024).
 */
export function ReportNoveltyModal({ isOpen, onClose, vehicle, onRegistrada }: ReportNoveltyModalProps) {
  const { activos: tipos, isLoading: cargandoTipos, error: errorTipos } = useLogbookTypes();

  const [entryTypeId, setEntryTypeId] = useState('');
  const [description, setDescription] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrada, setRegistrada] = useState<LogbookEntry | null>(null);

  // Las fotos elegidas, y las que NO llegaron. `pendientes` es lo que hace
  // posible reintentar: sin guardarlas, un fallo de red dejaría al operador
  // con la novedad registrada y la foto perdida.
  const [fotos, setFotos] = useState<File[]>([]);
  const [subidas, setSubidas] = useState(0);
  const [pendientes, setPendientes] = useState<File[]>([]);
  const { subir, subiendo, progreso } = useLogbookAttachments(null);

  useEffect(() => {
    if (!isOpen) return;
    setEntryTypeId('');
    setDescription('');
    setOccurredAt(toLocalInputValue(new Date().toISOString()));
    setError(null);
    setRegistrada(null);
    setEnviando(false);
    setFotos([]);
    setSubidas(0);
    setPendientes([]);
  }, [isOpen]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entryTypeId) { setError('Elige el tipo de novedad.'); return; }
    if (!description.trim()) { setError('Cuenta qué pasó: el tipo solo clasifica.'); return; }

    setEnviando(true);
    setError(null);
    try {
      const dto = await createLogbookEntry({
        entryTypeId,
        occurredAt: fromLocalInputValue(occurredAt) ?? new Date().toISOString(),
        description: description.trim(),
        // El `driverId` lo pone el servidor. Si no hay vehículo asignado, la
        // novedad cuelga solo del conductor — y eso es una asociación válida:
        // una sanción de tránsito tampoco tiene vehículo.
        vehicleId: vehicle?.id,
      });
      const entry = logbookEntryFromDto(dto);
      // 🚨 El éxito se muestra AQUÍ, antes de subir una sola foto. La novedad
      // ya existe y no se puede deshacer —no hay DELETE, y ADR-007 dice que la
      // bitácora no borra—, así que tratar un fallo de subida como "falló el
      // envío" haría que el operador la registrara otra vez. Las fotos se
      // informan aparte, en la misma pantalla de éxito.
      setRegistrada(entry);
      onRegistrada?.(entry);
      setEnviando(false);

      if (fotos.length > 0) {
        const { subidas: ok, fallidas } = await subir(fotos, entry.id);
        setSubidas(ok.length);
        setPendientes(fallidas);
      }
    } catch (err) {
      setError(mensajeErrorNovedad(err, 'registrar'));
      setEnviando(false);
    }
  };

  /** Reintenta SOLO lo que falta, contra la novedad que ya existe. */
  const reintentar = async () => {
    if (!registrada || pendientes.length === 0) return;
    const { subidas: ok, fallidas } = await subir(pendientes, registrada.id);
    setSubidas(n => n + ok.length);
    setPendientes(fallidas);
  };

  const handleClose = () => {
    setRegistrada(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Reportar novedad" size="sm">
      {registrada ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '1rem 0', textAlign: 'center' }}>
          <CheckCircle2 size={44} style={{ color: '#10B981' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
            Novedad registrada
          </h3>
          {/* La hora que se muestra es la que GUARDÓ el servidor, no la del
              reloj del navegador: si hubiera discrepancia, esta pantalla la
              enseña en vez de taparla. */}
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
            <strong>{registrada.entryTypeLabel}</strong> · ocurrió el {formatDateTime(registrada.occurredAt)}
            {registrada.vehiclePlate ? ` · ${registrada.vehiclePlate}` : ''}.
            <br />
            Queda registrada como novedad y tu supervisor la ve en su pantalla de Novedades.
          </p>

          {/* Las fotos se informan aparte de la novedad, y a propósito: la
              novedad YA quedó guardada pase lo que pase con ellas. Decir
              "error" aquí haría que el operador la reportara dos veces. */}
          {subiendo && (
            <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-secondary)' }}>
              Subiendo {progreso ? `foto ${progreso.actual} de ${progreso.total}` : 'las fotos'}…
            </p>
          )}

          {!subiendo && subidas > 0 && pendientes.length === 0 && (
            <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-secondary)' }}>
              {subidas === 1 ? '1 foto adjunta.' : `${subidas} fotos adjuntas.`}
            </p>
          )}

          {!subiendo && pendientes.length > 0 && (
            <div style={{
              width: '100%', textAlign: 'left',
              padding: '0.75rem 0.875rem', borderRadius: 'var(--radius-lg)',
              background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
            }}>
              <p style={{ margin: 0, fontSize: '12.5px', color: '#F59E0B', fontWeight: 600 }}>
                {pendientes.length === 1
                  ? '1 foto no se pudo subir.'
                  : `${pendientes.length} de ${pendientes.length + subidas} fotos no se pudieron subir.`}
              </p>
              <p style={{ margin: '0.25rem 0 0.625rem', fontSize: '11.5px', color: 'var(--text-secondary)' }}>
                La novedad quedó registrada igual. Puedes reintentar aquí mismo cuando vuelva la señal.
              </p>
              <Button variant="secondary" size="sm" onClick={reintentar} style={{ width: '100%' }}>
                <RefreshCw size={14} /> Reintentar {pendientes.length === 1 ? 'la foto' : 'las fotos'}
              </Button>
            </div>
          )}

          <Button
            variant="primary"
            size="sm"
            onClick={handleClose}
            disabled={subiendo}
            style={{ marginTop: '0.5rem', width: '100%' }}
          >
            {pendientes.length > 0 ? 'Cerrar sin la foto' : 'Cerrar'}
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>
          {vehicle ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
              <Truck size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />
              <div>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{vehicle.plate}</p>
                <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>{vehicle.brand} {vehicle.model}</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.625rem', padding: '0.75rem 0.875rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '12px' }}>
              <Info size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>No tienes vehículo asignado ahora mismo: la novedad quedará solo a tu nombre.</span>
            </div>
          )}

          <div>
            <p style={{ margin: '0 0 0.5rem', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>Tipo de novedad *</p>

            {cargandoTipos && (
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>Cargando tipos…</p>
            )}

            {errorTipos && (
              <p style={{ margin: 0, fontSize: '12px', color: '#EF4444' }}>
                No se pudo cargar el catálogo de tipos. Sin él no se puede registrar.
              </p>
            )}

            {/* El catálogo es del TENANT y lo ajusta un Administrador sin
                migración (ADR-015 §50). Pintar aquí una lista fija de cinco
                tipos —lo que había antes— era inventarle taxonomía a cada
                empresa. Los colores se asignan por `code`, el único eje
                estable; un tipo propio del tenant cae al neutro. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {tipos.map(t => {
                const color = colorDeTipo(t.code);
                const activo = entryTypeId === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setEntryTypeId(t.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.625rem 0.875rem', borderRadius: 'var(--radius-md)',
                      border: `2px solid ${activo ? color : 'var(--border)'}`,
                      background: activo ? `${color}14` : 'var(--bg-raised)',
                      color: activo ? color : 'var(--text-secondary)',
                      fontWeight: 600, fontSize: '12.5px', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Textarea
            label="¿Qué pasó? *"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Describe la novedad: qué pasó, dónde y qué hiciste."
            rows={3}
          />

          <div>
            <label
              htmlFor="novedad-ocurrio"
              style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '0 0 0.375rem', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}
            >
              <Clock size={13} style={{ color: 'var(--accent)' }} /> ¿Cuándo ocurrió?
            </label>
            <input
              id="novedad-ocurrio"
              type="datetime-local"
              value={occurredAt}
              onChange={e => setOccurredAt(e.target.value)}
              style={{
                width: '100%', padding: '0.5rem 0.75rem',
                background: 'var(--bg-raised)', color: 'var(--text-primary)',
                border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)',
                fontSize: '13px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <p style={{ margin: '0.375rem 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
              Por defecto, ahora. Si lo estás relatando después, pon la hora real: es la que decide
              a qué viaje pertenece.
            </p>
          </div>

          {/* BE-70 puso el backend: la foto se sube de verdad. Hasta el
              2026-09-21 este bloque estaba deshabilitado, y antes de FE-13 era
              peor —leía la foto con `FileReader` y la dejaba morir en memoria,
              así que el operador creía haberla enviado—.

              Las fotos se suben DESPUÉS de crear la novedad, porque el
              endpoint cuelga de su id. Ver `handleSend`. */}
          <div>
            <p style={{ margin: '0 0 0.375rem', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Camera size={14} style={{ color: 'var(--text-muted)' }} /> Adjuntar fotografía
            </p>
            <LogbookPhotoPicker files={fotos} onChange={setFotos} disabled={enviando} />
          </div>

          {error && (
            <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444', fontWeight: 600 }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '0.875rem' }}>
            <Button variant="ghost" size="sm" type="button" onClick={handleClose} style={{ flex: 1 }} disabled={enviando}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" type="submit" isLoading={enviando} disabled={tipos.length === 0} style={{ flex: 1.5, gap: '0.5rem' }}>
              <Send size={14} />
              Enviar novedad
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export function ReportNoveltyFAB({ onClick }: { onClick: () => void }) {
  return (
    <button
      id="report-novelty-fab"
      onClick={onClick}
      className="novelty-fab"
      title="Reportar novedad"
      aria-label="Reportar novedad"
    >
      <AlertTriangle size={22} />
      <span className="novelty-fab__label">Reportar Novedad</span>
    </button>
  );
}
